export function buildScopeTree(units) {
    const byId = new Map(units.map((unit) => [unit.id, unit]));
    const children = new Map();
    const roots = [];
    for (const unit of units) {
        /*
         * A unit whose parent is not in the list is a root *here*. That is not
         * only the company: somebody granted one district gets the company and
         * the region above it for context, but a person granted two branches in
         * different regions may get several disconnected tops.
         */
        if (unit.parentId && byId.has(unit.parentId)) {
            const siblings = children.get(unit.parentId);
            if (siblings)
                siblings.push(unit);
            else
                children.set(unit.parentId, [unit]);
        }
        else {
            roots.push(unit);
        }
    }
    for (const list of children.values())
        list.sort(compareUnits);
    roots.sort(compareUnits);
    const branchesUnder = new Map();
    const collect = (unit) => {
        const cached = branchesUnder.get(unit.id);
        if (cached)
            return cached;
        const found = unit.kind === 'branch' && unit.reachable ? [unit.id] : [];
        for (const child of children.get(unit.id) ?? [])
            found.push(...collect(child));
        branchesUnder.set(unit.id, found);
        return found;
    };
    for (const unit of units)
        collect(unit);
    const ancestorsOf = (unitId) => {
        const chain = [];
        const seen = new Set([unitId]);
        let at = byId.get(unitId)?.parentId ?? null;
        while (at && !seen.has(at)) {
            chain.push(at);
            seen.add(at);
            at = byId.get(at)?.parentId ?? null;
        }
        return chain;
    };
    const allBranches = units
        .filter((unit) => unit.kind === 'branch' && unit.reachable)
        .map((unit) => unit.id);
    return { byId, children, branchesUnder, ancestorsOf, allBranches, roots };
}
/** Company before region before district before branch, then by name. */
const RANK = { company: 0, region: 1, district: 2, branch: 3 };
function compareUnits(a, b) {
    return RANK[a.kind] - RANK[b.kind] || a.name.localeCompare(b.name);
}
/**
 * On, mixed or off — read from the branches, not from the roots.
 *
 * A unit is on when every reachable branch under it is covered, which is the
 * only definition that behaves when the same selection is expressed two ways
 * ("East Coast" and its four branches are the same tick). A unit with no
 * reachable branch under it — an empty region — is off and stays off; it
 * cannot be "fully selected" when there is nothing to select.
 */
export function stateOf(unitId, roots, tree) {
    const branches = tree.branchesUnder.get(unitId) ?? [];
    if (branches.length === 0)
        return 'off';
    const covered = new Set(coveredBranches(roots, tree));
    let hits = 0;
    for (const id of branches)
        if (covered.has(id))
            hits += 1;
    if (hits === 0)
        return 'off';
    return hits === branches.length ? 'on' : 'mixed';
}
/** How many of a unit's reachable branches this selection covers. */
export function countAt(unitId, roots, tree) {
    const branches = tree.branchesUnder.get(unitId) ?? [];
    const covered = new Set(coveredBranches(roots, tree));
    return { on: branches.filter((id) => covered.has(id)).length, total: branches.length };
}
/**
 * Tick or untick a unit, and say what the roots are afterwards.
 *
 * Expressed through the branches rather than by surgery on the root list: a
 * tick means "these branch ids join the covered set", an untick means "they
 * leave it", and `normalise` then finds the shortest way to say the result.
 * The surgical version — remove the subtree, re-add an ancestor's other
 * children — is where this kind of code goes wrong, because every shape of
 * tree is a separate case and the one nobody tested is the one that loses a
 * branch.
 */
export function toggle(unitId, roots, tree) {
    const branches = tree.branchesUnder.get(unitId) ?? [];
    if (branches.length === 0)
        return [...roots];
    const covered = new Set(coveredBranches(roots, tree));
    const turningOn = stateOf(unitId, roots, tree) !== 'on';
    for (const id of branches) {
        if (turningOn)
            covered.add(id);
        else
            covered.delete(id);
    }
    return normalise([...covered], tree);
}
/** Everything but this, dropped. */
export function only(unitId) {
    return [unitId];
}
/**
 * The shortest set of units that covers exactly these branches.
 *
 * Bottom-up: a unit whose every reachable branch is covered replaces those
 * branches in the answer, and its parent may then replace it in turn. So
 * ticking the last branch of a district collapses to the district, and
 * ticking the last district collapses to the region — which is what makes
 * "East Coast" survive as the stored answer rather than decaying into four
 * branch ids the first time somebody re-ticks one.
 */
export function normalise(selection, tree) {
    const covered = new Set();
    for (const id of selection) {
        for (const branch of tree.branchesUnder.get(id) ?? [])
            covered.add(branch);
    }
    if (covered.size === 0)
        return [];
    const chosen = [];
    const claimed = new Set();
    const walk = (unit) => {
        const branches = tree.branchesUnder.get(unit.id) ?? [];
        /*
         * Only a place the person reaches may stand for the ones under it.
         * Without that, somebody granted one district — who is shown the company
         * above it for context — would have their two branches collapse into the
         * company id, and the thing they ticked would be named as something they
         * cannot open.
         */
        const full = unit.reachable
            && branches.length > 0 && branches.every((id) => covered.has(id));
        if (full) {
            chosen.push(unit.id);
            for (const id of branches)
                claimed.add(id);
            return;
        }
        for (const child of tree.children.get(unit.id) ?? [])
            walk(child);
    };
    for (const root of tree.roots)
        walk(root);
    /*
     * A branch covered but not reached from any root — it can happen when a
     * stored selection outlives a move in the tree — is still the person's
     * selection, so it is kept rather than silently dropped.
     */
    for (const id of covered)
        if (!claimed.has(id))
            chosen.push(id);
    return chosen;
}
/**
 * What the server will actually narrow by: reachable branch ids.
 *
 * No roots means no branches, deliberately. "Nothing selected is everything"
 * reads like a kindness and is a trap: it makes ticking a region from a clean
 * slate an *untick* of everything else, because the model cannot tell "I have
 * not chosen yet" from "I have chosen all of it". The server resolves the
 * first case once, at the edge, by defaulting the roots to the top-most
 * places the person reaches — so by the time anything here sees a selection,
 * it says what it means.
 */
export function coveredBranches(roots, tree) {
    const out = new Set();
    for (const id of roots) {
        for (const branch of tree.branchesUnder.get(id) ?? [])
            out.add(branch);
    }
    return [...out];
}
/**
 * What the header says — §1.4. Places, never a sentence.
 *
 * The order matters. "Everything" is checked first and is said as the
 * company's name, because that is the word for it; but only for somebody who
 * actually reaches the company. A district manager whose whole reach is
 * selected sees their district, not a company they cannot open — the old
 * label told them they could see everything, which was true of their world
 * and false of the words.
 */
export function scopeLabelOf(roots, tree) {
    const reachableRoots = roots.filter((id) => tree.byId.get(id)?.reachable);
    const covered = coveredBranches(reachableRoots, tree);
    const everything = tree.allBranches.length > 0
        && covered.length === tree.allBranches.length;
    if (everything) {
        const top = topMostReachable(tree);
        if (top.length === 1)
            return { text: top[0].name, kind: top[0].kind };
        if (top.length > 1)
            return named(top);
    }
    const units = reachableRoots
        .map((id) => tree.byId.get(id))
        .filter((unit) => Boolean(unit))
        .sort(compareUnits);
    if (units.length === 0) {
        const top = topMostReachable(tree);
        return top[0] ? { text: top[0].name, kind: top[0].kind } : { text: '', kind: 'company' };
    }
    return named(units);
}
function named(units) {
    const first = units[0];
    if (units.length === 1)
        return { text: first.name, kind: first.kind };
    if (units.length === 2)
        return { text: `${first.name} + ${units[1].name}`, kind: first.kind };
    return { text: `${first.name} + ${units.length - 1} more`, kind: first.kind };
}
/** The highest places this person actually reaches — their own "everything". */
function topMostReachable(tree) {
    const out = [];
    const walk = (unit) => {
        if (unit.reachable) {
            out.push(unit);
            return;
        }
        for (const child of tree.children.get(unit.id) ?? [])
            walk(child);
    };
    for (const root of tree.roots)
        walk(root);
    return out.sort(compareUnits);
}
/**
 * What "everything" is, said as roots — the default when nobody has chosen.
 *
 * The company for most people; the one or two places they were granted for
 * somebody with partial access.
 */
export function everythingRoots(tree) {
    return topMostReachable(tree).map((unit) => unit.id);
}
//# sourceMappingURL=scope.js.map